import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  useTheme,
  alpha,
  LinearProgress,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  People,
  Settings,
  Analytics,
  Security,
  Warning,
  CheckCircle,
  TrendingUp,
  Memory,
  Speed,
  Shield,
  Update,
  Backup,
  AdminPanelSettings,
  Psychology,
  Refresh,
  Launch,
  ArrowForward,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

interface SystemHealth {
  status: 'excellent' | 'good' | 'warning' | 'critical';
  score: number;
  issues: string[];
}

interface QuickStat {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down' | 'stable';
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  icon: React.ReactElement;
}

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactElement;
  path: string;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  badge?: string;
}

const AdminDashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [systemHealth] = useState<SystemHealth>({
    status: 'excellent',
    score: 98,
    issues: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading system data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Access denied. This page is only available to administrators.
      </Alert>
    );
  }

  const quickStats: QuickStat[] = [
    {
      title: 'Total Users',
      value: 1247,
      change: '+12%',
      trend: 'up',
      color: 'primary',
      icon: <People />,
    },
    {
      title: 'Active Sessions',
      value: 89,
      change: '+5%',
      trend: 'up',
      color: 'success',
      icon: <TrendingUp />,
    },
    {
      title: 'AI Analyses Today',
      value: 156,
      change: '+23%',
      trend: 'up',
      color: 'secondary',
      icon: <Psychology />,
    },
    {
      title: 'System Load',
      value: '23%',
      change: '-8%',
      trend: 'down',
      color: 'info',
      icon: <Speed />,
    },
  ];

  const quickActions: QuickAction[] = [
    {
      title: 'User Management',
      description: 'Manage user accounts and permissions',
      icon: <People />,
      path: '/admin/users',
      color: 'primary',
      badge: '3 pending',
    },
    {
      title: 'System Settings',
      description: 'Configure AI models and system preferences',
      icon: <Settings />,
      path: '/admin/settings',
      color: 'secondary',
    },
    {
      title: 'Analytics Dashboard',
      description: 'View detailed system analytics and reports',
      icon: <Analytics />,
      path: '/analytics',
      color: 'success',
    },
    {
      title: 'Security Center',
      description: 'Monitor security events and audit logs',
      icon: <Security />,
      path: '/admin/security',
      color: 'error',
      badge: '2 alerts',
    },
  ];

  const systemAlerts = [
    {
      type: 'info' as const,
      message: 'System backup completed successfully',
      time: '2 hours ago',
      icon: <Backup />,
    },
    {
      type: 'warning' as const,
      message: 'High memory usage detected on server 2',
      time: '4 hours ago',
      icon: <Memory />,
    },
    {
      type: 'success' as const,
      message: 'AI model updated to version 2.1.0',
      time: '1 day ago',
      icon: <Update />,
    },
  ];

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'excellent': return theme.palette.success.main;
      case 'good': return theme.palette.info.main;
      case 'warning': return theme.palette.warning.main;
      case 'critical': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'excellent': return <CheckCircle />;
      case 'good': return <CheckCircle />;
      case 'warning': return <Warning />;
      case 'critical': return <Warning />;
      default: return <CheckCircle />;
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↗️';
      case 'down': return '↘️';
      case 'stable': return '➡️';
      default: return '➡️';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <AdminPanelSettings sx={{ fontSize: 60, color: theme.palette.primary.main }} />
        </motion.div>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Box>
        {/* Header Section */}
        <Fade in={true} timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  🛡️ Admin Dashboard
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Welcome back, {user?.username} • System Administrator
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2}>
                <Tooltip title="Refresh Dashboard">
                  <IconButton
                    sx={{
                      background: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.2),
                        transform: 'rotate(180deg)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                
                <Button
                  variant="contained"
                  startIcon={<Launch />}
                  onClick={() => navigate('/analytics')}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                    },
                  }}
                >
                  View Analytics
                </Button>
              </Stack>
            </Box>

            {/* System Health Status */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                background: `linear-gradient(135deg, ${alpha(getHealthColor(systemHealth.status), 0.1)} 0%, ${alpha(getHealthColor(systemHealth.status), 0.05)} 100%)`,
                border: `1px solid ${alpha(getHealthColor(systemHealth.status), 0.2)}`,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${getHealthColor(systemHealth.status)} 0%, ${alpha(getHealthColor(systemHealth.status), 0.7)} 100%)`,
                },
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: getHealthColor(systemHealth.status),
                        width: 64,
                        height: 64,
                      }}
                    >
                      {getHealthIcon(systemHealth.status)}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        System Health: {systemHealth.status.charAt(0).toUpperCase() + systemHealth.status.slice(1)}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Overall system performance score: {systemHealth.score}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={systemHealth.score}
                        sx={{
                          mt: 2,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: alpha(getHealthColor(systemHealth.status), 0.2),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getHealthColor(systemHealth.status),
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                  <Stack spacing={1}>
                    <Chip
                      icon={<Shield />}
                      label="All Systems Operational"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '0.9rem' }}
                    />
                    <Chip
                      icon={<Backup />}
                      label="Last Backup: 2h ago"
                      color="info"
                      variant="outlined"
                      sx={{ fontSize: '0.9rem' }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>

        {/* Quick Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {quickStats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={stat.title}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02, y: -4 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.1)} 0%, ${alpha(theme.palette[stat.color].main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette[stat.color].main, 0.2)}`,
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 12px 32px ${alpha(theme.palette[stat.color].main, 0.2)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: theme.palette[stat.color].main, width: 56, height: 56 }}>
                      {stat.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette[stat.color].main }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {stat.title}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {getTrendIcon(stat.trend)} {stat.change}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          vs last month
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          {/* Quick Actions */}
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              🚀 Quick Actions
            </Typography>
            <Grid container spacing={3}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} sm={6} key={action.title}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        background: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: `0 20px 40px ${alpha(theme.palette[action.color].main, 0.3)}`,
                          '& .action-icon': {
                            transform: 'scale(1.1) rotate(5deg)',
                          },
                          '& .action-arrow': {
                            transform: 'translateX(4px)',
                          },
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: theme.palette[action.color].main,
                        },
                      }}
                      onClick={() => navigate(action.path)}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar
                            className="action-icon"
                            sx={{
                              bgcolor: theme.palette[action.color].main,
                              width: 56,
                              height: 56,
                              transition: 'all 0.3s ease',
                              boxShadow: `0 8px 24px ${alpha(theme.palette[action.color].main, 0.3)}`,
                            }}
                          >
                            {action.icon}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {action.title}
                              </Typography>
                              {action.badge && (
                                <Chip
                                  label={action.badge}
                                  size="small"
                                  color={action.color}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                              {action.description}
                            </Typography>
                            <Button
                              variant="outlined"
                              color={action.color}
                              endIcon={<ArrowForward className="action-arrow" sx={{ transition: 'transform 0.3s ease' }} />}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                              }}
                            >
                              Open
                            </Button>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* System Alerts */}
          <Grid item xs={12} md={4}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              🔔 System Alerts
            </Typography>
            <Paper
              elevation={0}
              sx={{
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <List sx={{ p: 0 }}>
                <AnimatePresence>
                  {systemAlerts.map((alert, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <ListItem
                        sx={{
                          py: 2,
                          px: 3,
                          '&:hover': {
                            background: alpha(theme.palette.action.hover, 0.05),
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette[alert.type].main, 0.1),
                              color: theme.palette[alert.type].main,
                              width: 40,
                              height: 40,
                            }}
                          >
                            {alert.icon}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {alert.message}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {alert.time}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < systemAlerts.length - 1 && (
                        <Divider sx={{ mx: 3, opacity: 0.5 }} />
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </List>

              <Box sx={{ p: 2, textAlign: 'center', borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Button
                  variant="text"
                  size="small"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/admin/alerts')}
                  sx={{ textTransform: 'none' }}
                >
                  View All Alerts
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </motion.div>
  );
};

export default AdminDashboardPage;
